import React, {Fragment, useEffect, useState} from 'react';
import StepNavigation from './StepNavigation';
import StepQuestions from './StepQuestions';
import {TStepQuestions} from "../../types/TStepQuestions";
import {Questions} from "./Questions";
import {ProjectCostResult} from './ProjectCostResult';
import {toast} from 'react-toastify';
import {Transition} from '@headlessui/react';
import Image from "next/image";
import {transformImage} from "../../utils/StoryblokService";
import {SbBlokData} from "@storyblok/react/dist/types/types";
import {IStoryblokImage} from "../../types/IStoryblokImage";
import {storyblokEditable} from "@storyblok/react";

type TProjectCalculatorProps = {
    blok: { image: IStoryblokImage, form_image: IStoryblokImage } & SbBlokData
};

const ProjectCalculator = ({blok}: TProjectCalculatorProps) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [stepQuestions, setStepQuestions] = useState<TStepQuestions>(Questions);
    const [isComplete, setIsComplete] = useState(false);

    const handleInputChange = (event: any) => {
        if (event instanceof Event) {
            event.preventDefault();
        }

        const {name, value, type, checked} = event.target;
        setStepQuestions((prevData) => {

            const questionIndex = prevData.findIndex((question) => question.name === name);
            const question = prevData[questionIndex];

            if (type === 'checkbox') {

                const updatedFeatures = !checked
                    ? (question.value as Array<String>).filter((feature: any) => feature !== value)
                    : [...(question.value as Array<String>), value];

                prevData[questionIndex].value = updatedFeatures;

                return [...prevData];
            } else if (type === 'radio') {
                // Convert the string value to a boolean
                const boolValue = value === 'true';
                prevData[questionIndex].value = value;
                return [...prevData];
            } else {
                prevData[questionIndex].value = value;
                return [...prevData];
            }
        });
    };


    const handleNextStep = () => {
        document.querySelector('#projectSteps')?.scrollIntoView({
            behavior: 'smooth',
        });

        if (currentStep === 0) {
            const name = stepQuestions.find((question) => question.name === 'projectName')?.value as string;
            if (name.length === 0) {
                toast('Please enter a project name', {
                    position: "bottom-left",
                    type: "error"
                });
                return;
            }
        }
        if (currentStep === 1) {
            const features = stepQuestions.find((question) => question.name === 'features')?.value as Array<string>;
            if (features.length === 0) {
                toast('Please select at least one feature', {
                    position: "bottom-left",
                    type: "error"
                });
                return;
            }
        }
        setCurrentStep((prevStep) => prevStep + 1);
    };

    const handlePrevStep = () => {
        document.querySelector('#projectSteps')?.scrollIntoView({
            behavior: 'smooth',
        });

        setCurrentStep((prevStep) => prevStep - 1);
    };

    const handleComplete = () => {
        setIsComplete(true)
    };

    const image = (blok.image?.filename) ?
        <Image
            className="w-full bg-white rounded-lg"
            src={transformImage(blok.image.filename, '800x600')}
            width={800}
            height={600}
            alt=""
        /> : null;

    const formImage = (blok.form_image?.filename) ?
        <Image
            className="h-64 w-full bg-gray-50 object-cover sm:h-80 lg:absolute lg:h-full"
            src={transformImage(blok.form_image?.filename, '1200x2400')}
            width={600}
            height={1200}
            alt=""
        /> : <div></div>;

    return <React.Fragment>

        {!isComplete &&
            <div className=" py-16 sm:py-12 bg-white" {...storyblokEditable(blok)}>
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="md:col-span-1">
                            <p className="text-base font-semibold leading-7 text-secondary">
                                Quickly get an approximate figure for your Web-Development Project
                            </p>
                            <h2 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                Web-Development Project Cost Calculator
                            </h2>
                            <p className="mt-6 text-lg leading-8 text-gray-600">
                                To get started with our Project Cost Calculator, simply gather all the information you
                                have
                                about your project and enter it into the tool. It will provide you with an approximate
                                cost
                                for
                                your project, which can help you plan your budget more effectively.
                                <br/>
                                In addition, you can even download our project requirements template XLSX file! This
                                file is
                                designed to
                                serve as a starter kit for your project, making it easier for you to get started quickly
                                and
                                efficiently.
                            </p>
                        </div>

                        <div className={'md:col-span-1'}>
                            {blok.image?.filename &&
                                image}
                        </div>
                    </div>

                </div>
            </div>}

        <div className={"bg-white"}>
            <div className="mx-auto max-w-7xl px-6 lg:px-8">


                <Transition
                    show={isComplete}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <ProjectCostResult values={stepQuestions} formImage={formImage}/>
                </Transition>


                {!isComplete && (
                    <React.Fragment>
                        <StepNavigation
                            steps={['Project', 'Features', 'Budget', 'Contact']}
                            currentStep={currentStep}
                        />
                        <StepQuestions
                            questions={stepQuestions}
                            currentStep={currentStep}
                            handleChange={handleInputChange}
                        />
                        <div className="flex justify-between mt-4">
                            {currentStep > 0 && (
                                <button
                                    className="px-4 py-2 text-lg bg-primary text-white rounded shadow-md hover:bg-secondary mr-auto"
                                    onClick={handlePrevStep}
                                >
                                    &laquo;
                                    Previous
                                </button>
                            )}
                            {currentStep < 3 ? (
                                <button
                                    className="px-4 py-2 text-lg bg-primary text-white rounded shadow-md hover:bg-secondary ml-auto"
                                    onClick={handleNextStep}
                                >
                                    Next
                                    &raquo;
                                </button>
                            ) : (
                                <button
                                    className="px-4 py-2 text-lg bg-primary text-white rounded shadow-md hover:bg-secondary ml-auto"
                                    onClick={handleComplete}
                                >
                                    Complete
                                </button>
                            )}
                        </div>
                    </React.Fragment>
                )}

            </div>
        </div>
    </React.Fragment>;
};

export default ProjectCalculator;
